import { Component, Vue, Watch } from 'vue-property-decorator';
import { addDays, format, isAfter, isBefore, isFuture, startOfDay, subDays } from 'date-fns';
import { find, findIndex, sortBy } from 'lodash';
import CalendarJob from '@/model/entity/calendar/calendar-job';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Job from '@/model/entity/job/job';
import JobCard from '../job-card/index.vue';
import JobSearchConditionsInterface from '@/model/vo/job/job-search-conditions-interface';
import JobService from '@/model/service/job/job-service';
import JobStage from '@/model/entity/job/job-stage';
import Team from '@/model/entity/organisation/team';
import draggable from 'vuedraggable';

@Component({
    components:
    {
        'app-job-card': JobCard,
        draggable,
    },
})
export default class UserJobs extends Vue
{
    private userId: string|null = null;
    private clientId: number|null = null;
    private calendarJobs: CalendarJob[] = [];
    private jobs: Job[] = [];
    private team: Team|null = null;

    private isLoading = false;

    private stages: JobStage[] = [];
    private jobSearchExpandedIndex: number|null = null;
    private expandedStages: number[] = [];

    private searchTerm = '';
    private searchTimeout: number|null = null;
    private isSearching = false;
    private jobSearchResults: Job[] = [];

    private clientSelectorForm: FormInterface|null = null;

    private recentlyAddedJobsExpanded = 0;
    private recentlyUpdatedJobsExpanded = 0;

    private recentlyAddedJobs: Job[] = [];
    private recentlyAddedJobsTimeout: number|null = null;

    created()
    {
        if ('user_id' in this.$route.query)
        {
            this.userId = this.$route.query.user_id.toString();
        }
        else
        {
            this.userId = this.$user?.id ?? null;
        }

        this.initData();
    }

    destroyed()
    {
        // Stop checking for recently added jobs.
        if (this.recentlyAddedJobsTimeout)
        {
            window.clearTimeout(this.recentlyAddedJobsTimeout);
        }
    }

    private async initData()
    {
        this.initEvents();

        this.loadTeam();
        this.loadStages();
        this.loadRecentlyAdded();
        this.loadJobs();
        this.loadCalendarJobs();
    }

    @Watch('$route', {
        immediate: true,
        deep: true,
    })
    onTypeChanged()
    {
        const routeUserId = 'user_id' in this.$route.query ? this.$route.query.user_id as string : null;
        if (routeUserId !== this.userId)
        {
            this.userId = routeUserId ? routeUserId : this.$user?.id ?? null;
            this.loadTeam();
            this.loadRecentlyAdded();
            this.loadJobs();
        }
    }

    /**
     * Returns filtered added jobs - only those that haven't been scheduled or set to Waiting for Client / Acceptance Testing
     */

    private get filteredRecentlyAddedJobs(): Job[]
    {
        const jobs: Job[] = [];

        for (const job of this.recentlyAddedJobs)
        {
            // Ignore jobs that are not in a V+ stage
            if (!this.isVerticalPlusStageJob(job))
            {
                continue;
            }

            // Ignore jobs that are not recently added
            if (!this.isRecentlyAdded(job))
            {
                continue;
            }

            // Add to the array
            jobs.push(job);
        }

        return jobs;
    }

    /**
     * Returns filtered added jobs - only those that haven't been scheduled or set to Waiting for Client / Acceptance Testing
     */

    private get filteredRecentlyUpdatedJobs(): Job[]
    {
        const jobs: Job[] = [];

        for (const job of this.recentlyAddedJobs)
        {
            // Ignore jobs that are not in a V+ stage
            if (!this.isVerticalPlusStageJob(job))
            {
                continue;
            }

            // Ignore jobs that are recently added
            if (this.isRecentlyAdded(job))
            {
                continue;
            }

            // Add to the array
            jobs.push(job);
        }

        return jobs;
    }

    /**
     * Returns the added limit
     */

    private get recentlyAddedLimit(): Date
    {
        const today = startOfDay(new Date());
        const day = parseInt(format(today, 'i'));

        switch (day)
        {
        // Monday, use previous Friday
        case 1:
            return subDays(today, 3);

            // Sunday, user previous Friday
        case 7:
            return subDays(today, 2);

            // Use previous working day
        default:
            return subDays(today, 1);
        }
    }

    /**
     * Returns whether the job is scheduled in the future
     *
     * @param job
     */

    private getJobIsScheduled(job: Job): boolean
    {
        for (const calendarJob of this.calendarJobs)
        {
            // Skip if not this job
            if (calendarJob.jobId !== job.id)
            {
                continue;
            }

            // If it's scheduled for today then consider it scheduled even if the exact time has passed
            // Therefore just add 1 day to every job as an easy hack!
            const dateFrom = startOfDay(addDays(calendarJob.dateTo, 1));

            if (calendarJob.userId === this.userId && isFuture(dateFrom))
            {
                return true;
            }
        }

        return false;
    }

    @Watch('searchTerm')
    private onSearchTermChanged()
    {
        if (this.searchTimeout)
        {
            window.clearTimeout(this.searchTimeout);
        }

        window.setTimeout(() =>
        {
            this.findJobs();
        }, 500);
    }

    private onClientChanged(values: FormFieldValuesInterface)
    {
        this.clientId = values.client as number;

        this.loadRecentlyAdded();
        this.loadJobs();
    }

    @Watch('visibleStages')
    private onVisibleStagesChanged()
    {
        // Expand all stages by default
        this.expandedStages = [];
        for (let index = 0; index < this.visibleStages.length; index++)
        {
            this.expandedStages.push(index);
        }
    }

    /**
     * Returns the jobs arranged by stage
     */

    private get stageJobs(): {[stageId: number]: Job[]}
    {
        // Assign jobs into stages
        const stageJobs: {[stageId: number]: Job[]} = {};

        for (const job of this.jobs)
        {
            if (!job.stageId)
            {
                continue;
            }

            if (!(job.stageId in stageJobs))
            {
                stageJobs[job.stageId] = [];
            }

            stageJobs[job.stageId].push(job);
        }

        return stageJobs;
    }

    /**
     * Returns the visible stages
     */

    private get visibleStages(): JobStage[]
    {
        let stages: JobStage[] = [];

        for (const stage of this.stages)
        {
            if (!stage.id)
            {
                continue;
            }

            if (stage.id in this.stageJobs)
            {
                stages.push(stage);
            }
        }

        // Sort by calendar order
        stages = sortBy(stages,
            [ (stage: JobStage) => stage.calendarOrder ]);

        return stages;
    }

    /**
     * Initialises events
     */

    private initEvents()
    {
        EventBus.instance.$on('calendar-job-created', (calendarJob: CalendarJob) =>
        {
            this.handleUpdatedCalendarJob(calendarJob);
        });
        EventBus.instance.$on('calendar-job-updated', (calendarJob: CalendarJob) =>
        {
            this.handleUpdatedCalendarJob(calendarJob);
        });
        EventBus.instance.$on('calendar-job-deleted', (calendarJob: CalendarJob) =>
        {
            this.handleDeletedCalendarJob(calendarJob);
        });
    }

    /**
     * Handles a calendar job being created / updated
     *
     * @param calendarJob
     */

    private handleUpdatedCalendarJob(calendarJob: CalendarJob)
    {
        this.updateJobFromCalendarJob(this.jobs, calendarJob);
        this.updateJobFromCalendarJob(this.recentlyAddedJobs, calendarJob);
    }

    /**
     * Handles a calendar job being deleted
     *
     * @param calendarJob
     */

    private handleDeletedCalendarJob(calendarJob: CalendarJob)
    {
        this.updateJobFromCalendarJob(
            this.jobs, calendarJob, true,
        );
        this.updateJobFromCalendarJob(
            this.recentlyAddedJobs, calendarJob, true,
        );
    }

    /**
     * Updates a job from a calendar job
     *
     * @param jobs
     * @param calendarJob
     */

    private updateJobFromCalendarJob(
        jobs: Job[], calendarJob: CalendarJob, removed = false,
    )
    {
        // Find a matching job
        const jobIndex = findIndex(jobs, { id: calendarJob.job?.id });
        if (jobIndex > -1)
        {
            const job = jobs[jobIndex];

            // See if the calendar job already exists and just needs updating
            const existingCalendarJobIndex = findIndex(this.calendarJobs, { id: calendarJob.id });
            if (existingCalendarJobIndex > -1)
            {
                if (removed)
                {
                    this.calendarJobs.splice(existingCalendarJobIndex, 1);
                }
                else
                {
                    Vue.set(
                        this.calendarJobs, existingCalendarJobIndex, calendarJob,
                    );
                }
            }
            else if (!removed)
            {
                this.calendarJobs.push(calendarJob);
            }

            Vue.set(
                jobs, jobIndex, job,
            );
        }
    }

    /**
     * Loads future calendar jobs
     */

    private async loadCalendarJobs()
    {
        if (!this.userId)
        {
            return;
        }

        const results = await this.$serviceContainer.calendarService.findCalendarJobs({
            'user': this.userId,
            'dateFrom[after]': format(new Date(), 'yyyy-MM-dd'),
            'itemsPerPage': 1000,
            'order': [
                {
                    key: 'dateFrom',
                    descending: false,
                },
            ],
        });

        this.calendarJobs = results.results;
    }

    /**
     * Loads the job stages
     */

    private async loadStages()
    {
        const results = await this.$serviceContainer.jobService.findJobStages({
            'calendarOrder[gt]': 0,
            'deleted': false,
            'order': [
                {
                    key: 'calendarOrder',
                    descending: false,
                },
                {
                    key: 'displayOrder',
                    descending: false,
                },
            ],
        });
        this.stages = results.results;
    }

    /**
     * Loads the jobs assigned to the current user
     */

    private async loadJobs()
    {
        this.isLoading = true;

        const searchConditions: JobSearchConditionsInterface =
        {
            assignee: this.userId ?? undefined,
            client: this.clientId ?? undefined,
            complete: false,
            itemsPerPage: 1000,
            page: 1,
            order: [
                {
                    key: 'name',
                    descending: false,
                },
            ],
        };

        const results = await this.$serviceContainer.jobService.findJobs(searchConditions);
        this.jobs = results.results;

        this.isLoading = false;
    }

    /**
     * Loads recently added jobs to the current user
     */

    private async loadRecentlyAdded()
    {
        if (this.recentlyAddedJobsTimeout)
        {
            window.clearTimeout(this.recentlyAddedJobsTimeout);
        }

        try
        {
            const searchConditions: JobSearchConditionsInterface =
            {
                recentlyUpdated: true,
                ignoreSelfUpdate: true,
                assignee: this.userId ?? undefined,
                client: this.clientId ?? undefined,
                complete: false,
                itemsPerPage: 1000,
                page: 1,
                order: [
                    {
                        key: 'name',
                        descending: false,
                    },
                ],
            };

            const results = await this.$serviceContainer.jobService.findJobs(searchConditions);
            this.recentlyAddedJobs = results.results;
        }
        catch (error)
        {
            //
        }
        finally
        {
            this.recentlyAddedJobsTimeout = window.setTimeout(() => this.loadRecentlyAdded(), 60000);
        }
    }

    /**
     * Loads the team based for the current user
     */

    private async loadTeam()
    {
        if (!this.userId)
        {
            return;
        }

        const team = await this.$serviceContainer.userService.getUserLegacyTeam(this.userId);
        if (team)
        {
            this.team = team;
        }
        else
        {
            this.team = null;
        }

        this.generateClientSelectorForm();
    }

    /**
     * Searches jobs
     */

    private async findJobs()
    {
        if (!this.searchTerm)
        {
            this.jobSearchResults = [];
            return;
        }

        this.isSearching = true;

        const searchConditions: JobSearchConditionsInterface =
        {
            searchTerm: this.searchTerm,
            complete: false,
            itemsPerPage: 10,
            page: 1,
        };

        const results = await this.$serviceContainer.jobService.findJobs(searchConditions);
        this.jobSearchResults = results.results;

        this.isSearching = false;
    }

    /**
     * Generates the quick edit form
     *
     * @returns
     */

    private generateClientSelectorForm()
    {
        const form: FormInterface =
        {
            items: [
                {
                    key: 'client',
                    type: 'integer',
                    title: this.$t('general.field.client').toString(),
                    value: this.clientId,
                    required: false,
                    clearable: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientFormOptionsCallback(
                        search,
                        search ? null : (this.team?.id ?? null),
                        search ? null : 100,
                    ),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientFormTitlesCallback(values),
                    cacheBuster: new Date().getTime()
                        .toString(),
                },
            ],
        };

        this.clientSelectorForm = Object.assign({}, form);
    }

    /**
     * Handles job preview requests
     *
     * @param job
     * @param event
     */

    private onPreviewJob(job: Job, event: any)
    {
        EventBus.instance.$emit('preview-job', job.id);
    }

    /**
     * Handles job open requests
     *
     * @param job
     * @param event
     */

    private onOpenJob(job: Job, event: any)
    {
        EventBus.instance.$emit(
            'open-job', job.id, true,
        );
    }

    /**
     * Handles a job being added to another stage.
     *
     * @param newStage
     * @param event
     */

    private async onJobDropped(newStage: JobStage, event: any)
    {
        // Get the job id
        const jobId = parseInt(event.item.dataset.jobId);

        // If this is a calendar job then delete it
        if (event.item.dataset.calendarJobId)
        {
            // Get the calendar job id
            const calendarJobId = parseInt(event.item.dataset.calendarJobId);

            // Delete the calendar job
            await this.$serviceContainer.calendarService.deleteCalendarJob(calendarJobId);

            // Remove the element from the calendar component
            EventBus.instance.$emit(
                'calendar-job-drop-deleted', calendarJobId, jobId,
            );

            // Remove the dom element, only if not coming from the month draggable which is vue controlled
            if (!event.from.classList.contains('calendar__draggable--month'))
            {
                event.item.remove();
            }
        }

        // Otherwise change the job stage
        else
        {
            // Update the stage on the job
            const job = find(this.jobs, { id: jobId });
            if (!job)
            {
                return;
            }

            job.stageId = newStage.id;

            // Update the database
            await this.$serviceContainer.jobService.updateJob(job);

            // Remove the dom element
            event.clone.remove();
        }
    }

    /**
     * Returns whether the given job is in a V+ stage
     *
     * @param job
     * @returns
     */

    private isVerticalPlusStageJob(job: Job): boolean
    {
        if (!job.stage?.id)
        {
            return true;
        }

        // If AT / WFC then no
        if ([ JobService.STAGE_ACCEPTANCE_TESTING, JobService.STAGE_WAITING_FOR_CLIENT ].includes(job.stage.id))
        {
            return false;
        }

        // Else yes
        else
        {
            return true;
        }
    }

    /**
     * Returns whether the job has recently been added
     *
     * @param job
     * @returns
     */

    private isRecentlyAdded(job: Job): boolean
    {
        // Recently added if created on or after the date limit and not been recently re-assigned
        if (
            // Created ON or AFTER the date limit (i.e. recently added)
            !isBefore(job.dateCreated, this.recentlyAddedLimit)

            &&

            // Not been re-assigned since the created date
            !(job.assignedDate && isAfter(job.assignedDate, job.dateCreated))
        )
        {
            return true;
        }

        // Else not and should be in Recently updated instead
        else
        {
            return false;
        }
    }
}